import type { AcademicArticleDownloadPayload, BookDownloadPayload, CheckedDownloadItem, DownloadTreeNode, InclusionOptions, PdfNode } from '~/types/download'
import { isAcademicArticlePayload } from '~/utils/download/isAcademicArticlePayload'
import { isBookPayload } from '~/utils/download/isBookPayload'

export type DownloadFormat = 'pdf' | 'docx'

export interface DownloadItem {
  id?: string
  ezMainNodeId: number
}

interface DownloadRequestByItems {
  items: CheckedDownloadItem[]
  format: DownloadFormat
  options?: Partial<InclusionOptions>
  toc?: DownloadTreeNode[]
}

function buildDefaultInclusionOptions(options?: Partial<InclusionOptions>): InclusionOptions {
  return {
    lovtekst: options?.lovtekst ?? false,
    curatedReferences: options?.curatedReferences ?? false,
    frontPage: options?.frontPage ?? true,
    tableOfContents: options?.tableOfContents,
    colophonPage: options?.colophonPage,
    markings: options?.markings ?? false,
    specificPageNumbers: options?.specificPageNumbers,
  }
}

export function useDownloadApi() {
  function extractCuratedReferences(payload?: DownloadTreeNode['payload']) {
    if (payload && typeof payload === 'object' && 'curatedReferences' in payload) {
      return (payload as { curatedReferences?: PdfNode['curatedReferences'] }).curatedReferences
    }
    return undefined
  }

  const mapTocItem = (t: DownloadTreeNode): PdfNode => ({
    id: t.downloadId ?? t.id,
    source: t.source ?? '',
    title: t.title ?? '',
    authors: t.authors ?? undefined,
    lastUpdated: t.lastUpdated ?? null,
    curatedReferences: extractCuratedReferences(t.payload),
    nodeType: t.nodeType ?? t.type ?? '',
    children: t.children?.map(mapTocItem) || [],
    pageNumber: t.pageNumber,
  })

  async function sendDownloadRequest(endpoint: string, body: Record<string, unknown>, format: DownloadFormat) {
    const res = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })

    if (!res.ok) {
      const ct = res.headers.get('content-type') || ''
      let msg = `Download failed (${res.status})`
      if (ct.includes('application/json')) {
        try {
          const data = await res.json()
          msg = data?.statusMessage || data?.message || msg
        }
        catch {}
      }
      else {
        try {
          const text = await res.text()
          if (text) {
            msg = `${msg}: ${text}`
          }
        }
        catch {}
      }

      throw new Error(msg)
    }

    const contentDisposition = res.headers.get('content-disposition')
    let filename = `download.${format}`

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="([^"]+)"/)
      if (filenameMatch?.[1]) {
        filename = filenameMatch[1]
      }
    }

    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    try {
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      a.style.display = 'none'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    }
    finally {
      URL.revokeObjectURL(url)
    }
  }

  function buildLawRequestBody(params: DownloadRequestByItems) {
    const missingEzMainNodeIds = params.items
      .filter(item => typeof item.ezMainNodeId !== 'number' || Number.isNaN(item.ezMainNodeId))
      .map(item => item.id)

    if (missingEzMainNodeIds.length > 0) {
      throw new Error(`Nedlasting krever ezMainNodeId for valgte noder. Mangler for: ${missingEzMainNodeIds.join(', ')}`)
    }

    return {
      items: params.items.map(item => ({ id: item.id, ezMainNodeId: item.ezMainNodeId })),
      format: params.format,
      options: buildDefaultInclusionOptions(params.options),
      toc: params.toc?.map(mapTocItem) || [],
    }
  }

  function buildBookRequestBody(params: DownloadRequestByItems, bookItems: CheckedDownloadItem[]) {
    const firstPayload = bookItems[0]?.payload
    if (!isBookPayload(firstPayload)) {
      throw new Error('Manglende bokdata for nedlasting.')
    }

    const inconsistentBook = bookItems.some(item => !isBookPayload(item.payload) || item.payload.bookId !== firstPayload.bookId)
    if (inconsistentBook) {
      throw new Error('Kan ikke laste ned innhold fra flere bøker samtidig.')
    }

    return {
      items: bookItems.map((item) => {
        const payload = item.payload as BookDownloadPayload
        return {
          id: item.id,
          bookId: payload.bookId,
          tocId: payload.tocId,
          title: payload.title ?? undefined,
          pageNumber: payload.pageNumber ?? undefined,
          fragmentId: payload.fragmentId ?? undefined,
          nextPageNumber: payload.nextPageNumber ?? undefined,
          nextFragmentId: payload.nextFragmentId ?? undefined,
        }
      }),
      format: params.format,
      options: buildDefaultInclusionOptions(params.options),
      toc: params.toc?.map(mapTocItem) || [],
      book: {
        id: firstPayload.bookId,
        title: firstPayload.bookTitle || 'Bok',
        subtitle: firstPayload.bookSubtitle ?? null,
        authors: firstPayload.authors ?? undefined,
      },
    }
  }

  function buildAcademicArticleRequestBody(params: DownloadRequestByItems, articleItems: CheckedDownloadItem[]) {
    const firstPayload = articleItems[0]?.payload
    if (!isAcademicArticlePayload(firstPayload)) {
      throw new Error('Manglende fagartikkeldata for nedlasting.')
    }

    if (params.format !== 'pdf') {
      throw new Error('Nedlasting av fagartikler støtter forelopig kun PDF.')
    }

    const inconsistentArticle = articleItems.some((item) => {
      if (!isAcademicArticlePayload(item.payload)) {
        return true
      }

      return item.payload.articleId !== firstPayload.articleId
    })

    if (inconsistentArticle) {
      throw new Error('Kan ikke laste ned innhold fra flere fagartikler samtidig.')
    }

    return {
      items: articleItems.map((item) => {
        const payload = item.payload as AcademicArticleDownloadPayload
        return {
          id: item.id,
          title: payload.sectionTitle ?? undefined,
        }
      }),
      format: params.format,
      options: buildDefaultInclusionOptions(params.options),
      // Academic article download does not use TOC content server-side.
      // Avoid sending HTML-rich bodytext in request payload because the global
      // XSS validator can reject transformed HTML before the route handler runs.
      toc: [],
      article: {
        id: firstPayload.articleId,
        title: firstPayload.articleTitle || 'Fagartikkel',
        subtitle: firstPayload.articleSubtitle ?? null,
        authors: firstPayload.authors ?? undefined,
        lastChanged: firstPayload.articleLastChanged ?? null,
      },
    }
  }

  async function downloadFile(params: DownloadRequestByItems) {
    const bookItems = params.items.filter(item => isBookPayload(item.payload))
    const articleItems = params.items.filter(item => isAcademicArticlePayload(item.payload))

    if (bookItems.length > 0) {
      if (bookItems.length !== params.items.length) {
        throw new Error('Ugyldige nedlastingsparametere.')
      }

      const body = buildBookRequestBody(params, bookItems)
      await sendDownloadRequest('/api/download/book', body, params.format)
      return
    }

    if (articleItems.length > 0) {
      if (articleItems.length !== params.items.length) {
        throw new Error('Ugyldige nedlastingsparametere.')
      }

      const body = buildAcademicArticleRequestBody(params, articleItems)
      await sendDownloadRequest('/api/download/academicArticle', body, params.format)
      return
    }

    const body = buildLawRequestBody(params)
    await sendDownloadRequest('/api/download/law-and-commentary', body, params.format)
  }

  return {
    downloadFile,
  }
}
